import React from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { 
  HomeIcon, 
  UserGroupIcon, 
  DocumentTextIcon, 
  UsersIcon, 
  ChatBubbleLeftRightIcon, 
  ChartBarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: {
    name: string;
    role: string;
    email: string;
  };
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, currentUser }) => {
  const location = useLocation();
  const { tenantSlug = 'default-tenant' } = useParams();
  const isCrmSelected = location.pathname.includes('/crm') || location.pathname.includes('/leads') || 
                        location.pathname.includes('/applications') || location.pathname.includes('/stakeholders') || 
                        location.pathname.includes('/communications') || location.pathname.includes('/contact-messages') || 
                        location.pathname.includes('/analytics') || location.pathname.includes('/admissions-game');

  // Top Platform Modules removed as per request

  const crmNavigation = [
    { name: 'CRM Dashboard', href: '/dashboard', icon: HomeIcon, color: 'from-blue-500 to-indigo-600', bgColor: 'bg-blue-50' },
    { name: 'Lead Management', href: '/leads', icon: UserGroupIcon, color: 'from-emerald-500 to-teal-600', bgColor: 'bg-emerald-50' },
    { name: 'Applications', href: '/applications', icon: DocumentTextIcon, color: 'from-purple-500 to-pink-600', bgColor: 'bg-purple-50' },
    { name: 'Stakeholders', href: '/stakeholders', icon: UsersIcon, color: 'from-orange-500 to-red-600', bgColor: 'bg-orange-50' },
    { name: 'Communications', href: '/communications', icon: ChatBubbleLeftRightIcon, color: 'from-cyan-500 to-blue-600', bgColor: 'bg-cyan-50' },
    { name: 'Contact Messages', href: '/contact-messages', icon: ChatBubbleLeftRightIcon, color: 'from-indigo-500 to-purple-600', bgColor: 'bg-indigo-50' },
    { name: 'Analytics', href: '/analytics', icon: ChartBarIcon, color: 'from-amber-500 to-yellow-600', bgColor: 'bg-amber-50' },
    { name: 'AI Analytics', href: '/ai-analytics', icon: ChartBarIcon, color: 'from-purple-500 to-indigo-600', bgColor: 'bg-purple-50' },
  ];

  // Admissions Game navigation
  const admissionsGameNavigation = [
    { name: 'Team Competition', href: '/admissions-game/teams', icon: require('@heroicons/react/24/outline').TrophyIcon, color: 'from-yellow-500 to-orange-600', bgColor: 'bg-yellow-50' },
    { name: 'Leaderboard', href: '/admissions-game/leaderboard', icon: require('@heroicons/react/24/outline').ChartBarIcon, color: 'from-green-500 to-emerald-600', bgColor: 'bg-green-50' },
    { name: 'My Wallet', href: '/admissions-game/wallet', icon: require('@heroicons/react/24/outline').CurrencyRupeeIcon, color: 'from-emerald-500 to-teal-600', bgColor: 'bg-emerald-50' },
    { name: 'Referral Tracking', href: '/admissions-game/referrals', icon: require('@heroicons/react/24/outline').UserPlusIcon, color: 'from-blue-500 to-indigo-600', bgColor: 'bg-blue-50' },
  ];

  // Admin navigation (only for Superadmin and Admin)
  const adminNavigation = currentUser.role === 'superadmin' || currentUser.role === 'admin' ? [
    { name: 'Super Admin', href: '/super-admin', icon: require('@heroicons/react/24/outline').ShieldCheckIcon, color: 'from-blue-600 to-indigo-800', bgColor: 'bg-blue-50' },
    { name: 'Schools', href: '/super-admin/schools', icon: require('@heroicons/react/24/outline').BuildingOfficeIcon, color: 'from-indigo-600 to-purple-800', bgColor: 'bg-indigo-50' },
    { name: 'Onboarding', href: '/super-admin/onboarding', icon: require('@heroicons/react/24/outline').DocumentCheckIcon, color: 'from-purple-600 to-fuchsia-800', bgColor: 'bg-purple-50' },
    { name: 'Platform Overview', href: '/super-admin/platform', icon: require('@heroicons/react/24/outline').ServerStackIcon, color: 'from-fuchsia-600 to-pink-800', bgColor: 'bg-fuchsia-50' },
    { name: 'Performance', href: '/super-admin/performance', icon: require('@heroicons/react/24/outline').ChartBarIcon, color: 'from-pink-600 to-rose-800', bgColor: 'bg-pink-50' },
    { name: 'User Management', href: '/users', icon: require('@heroicons/react/24/outline').Cog6ToothIcon, color: 'from-slate-600 to-gray-800', bgColor: 'bg-slate-50' },
    { name: 'System Activity', href: '/system-activity', icon: require('@heroicons/react/24/outline').ClockIcon, color: 'from-purple-600 to-indigo-800', bgColor: 'bg-purple-50' },
  ] : [];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-[60px] left-0 z-30 w-64 glass-sidebar-blue transform transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        shadow-2xl shadow-blue-950/40
      `} style={{ height: 'calc(100vh - 60px)' }}>
        <div className="flex flex-col h-full">
          {/* Close button for mobile */}
          <div className="flex justify-end p-4 lg:hidden">
            <button
              onClick={onClose}
              className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 pb-4 space-y-1 overflow-y-auto">
            {/* CRM Primary Navigation Section */}
            <div className="mb-6">
              <p className="text-[10px] font-black text-blue-200/50 uppercase tracking-[0.25em] mb-4 px-3 mt-4">
                CRM Engine
              </p>
              <div className="space-y-1.5">
                {crmNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `group flex items-center px-4 py-3 text-sm font-bold rounded-2xl transition-all duration-300 ${
                        isActive
                          ? 'bg-white text-blue-900 shadow-xl scale-[1.02]'
                          : 'text-blue-50/80 hover:bg-white/10 hover:text-white'
                      }`
                    }
                    onClick={() => window.innerWidth < 1024 && onClose()}
                  >
                    {({ isActive }: { isActive: boolean }) => (
                      <>
                        <div className={`p-2 rounded-xl mr-3 flex-shrink-0 transition-all duration-300 ${isActive ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-100/60 group-hover:bg-white/20 group-hover:text-white'}`}>
                          <item.icon className="h-4 w-4" />
                        </div>
                        <span className="tracking-tight">{item.name}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>

              {/* Admissions Game Section Section */}
              <div className="mt-8 mb-4 px-3">
                <p className="text-[10px] font-black text-amber-400 uppercase tracking-[0.25em] flex items-center">
                  <span className="mr-2 text-base">🎮</span> Admissions Game
                </p>
              </div>
              <div className="space-y-1.5">
                {admissionsGameNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `group flex items-center px-4 py-3 text-sm font-bold rounded-2xl transition-all duration-300 ${
                        isActive
                          ? 'bg-amber-400 text-amber-950 shadow-xl scale-[1.02]'
                          : 'text-blue-50/80 hover:bg-white/10 hover:text-white'
                      }`
                    }
                    onClick={() => window.innerWidth < 1024 && onClose()}
                  >
                    {({ isActive }: { isActive: boolean }) => (
                      <>
                        <div className={`p-2 rounded-xl mr-3 flex-shrink-0 transition-all duration-300 ${isActive ? 'bg-amber-900/20 text-amber-950' : 'bg-white/5 text-blue-100/60 group-hover:bg-white/20 group-hover:text-white'}`}>
                          <item.icon className="h-4 w-4" />
                        </div>
                        <span>{item.name}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>

            {/* Admin Navigation Section Section */}
            {adminNavigation.length > 0 && (
              <div className="mt-8">
                <p className="text-[10px] font-black text-blue-200/50 uppercase tracking-[0.25em] mb-4 px-3">
                  Administration
                </p>
                <div className="space-y-1.5">
                  {adminNavigation.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        `group flex items-center px-4 py-3 text-sm font-bold rounded-2xl transition-all duration-300 ${
                          isActive
                            ? 'bg-white/20 text-white shadow-xl scale-[1.02]'
                            : 'text-blue-50/80 hover:bg-white/10 hover:text-white'
                        }`
                      }
                      onClick={() => window.innerWidth < 1024 && onClose()}
                    >
                      {({ isActive }: { isActive: boolean }) => (
                        <>
                          <div className={`p-2 rounded-xl mr-3 flex-shrink-0 transition-all duration-300 ${isActive ? 'bg-white text-blue-900' : 'bg-white/5 text-blue-100/60 group-hover:bg-white/20 group-hover:text-white'}`}>
                            <item.icon className="h-4 w-4" />
                          </div>
                          <span>{item.name}</span>
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </nav>

          {/* User Profile at Bottom - Glassy Plinth */}
          <div className="p-4 mt-auto">
            <div className="bg-white/5 backdrop-blur-md rounded-3xl p-4 border border-white/10 flex items-center group cursor-pointer hover:bg-white/10 transition-all">
              <div className="h-10 w-10 flex-shrink-0 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white font-black shadow-lg">
                {currentUser.name.charAt(0)}
              </div>
              <div className="ml-3 overflow-hidden">
                <p className="text-sm font-black text-white truncate">{currentUser.name}</p>
                <p className="text-[10px] text-blue-200/60 font-bold uppercase tracking-widest truncate">
                  {currentUser.role.replace('_', ' ')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>
      <div className="lg:hidden h-[60px]" />
    </>
  );
};

export default Sidebar;